import React from 'react';
import { usePricing } from '../contexts/PricingContext';

export const StrikeCards = ({ selectedType, onStrikeSelect, selectedStrike }) => {
  const { strikes, btcPrice, dataSource, lastStrikeUpdate } = usePricing();

  console.log('📊 StrikeCards RENDER:', {
    strikes: strikes.length,
    selectedType,
    btcPrice,
    lastUpdate: lastStrikeUpdate,
    strikesData: strikes // CRITICAL: Log actual strikes data
  });

  // CRITICAL: Force display even if loading
  if (!strikes || strikes.length === 0) {
    return (
      <div className="text-center text-gray-400 py-8">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <div>Loading strikes... (BTC: ${btcPrice})</div>
        <div className="text-xs mt-2">Last update: {lastStrikeUpdate || 'Never'}</div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    try {
      if (value === null || value === undefined || value === '' || isNaN(value)) return '$0.00';
      const num = Number(value);
      if (!isFinite(num)) return '$0.00';
      return `$${num.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}`;
    } catch (error) {
      return '$0.00';
    }
  };

  const getMoneynessStyling = (moneyness) => {
    switch(moneyness) {
      case 'ITM':
        return {
          bg: 'bg-green-900/60',
          border: 'border-green-500',
          text: 'text-green-200',
          badge: 'bg-green-600 text-green-100'
        };
      case 'ATM':
        return {
          bg: 'bg-purple-900/60',
          border: 'border-purple-500',
          text: 'text-purple-200',
          badge: 'bg-purple-600 text-purple-100'
        };
      case 'OTM':
        return {
          bg: 'bg-red-900/60',
          border: 'border-red-500',
          text: 'text-red-200',
          badge: 'bg-red-600 text-red-100'
        };
      default:
        return {
          bg: 'bg-gray-900/60',
          border: 'border-gray-500',
          text: 'text-gray-200',
          badge: 'bg-gray-600 text-gray-100'
        };
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-medium">Strike Prices</h3>
        <div className="text-xs text-gray-500">
          BTC: ${btcPrice} • {strikes.length} strikes
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {strikes.map((strike, index) => {
          // CRITICAL: Robust data validation
          if (!strike || typeof strike !== 'object') {
            console.warn('Invalid strike data:', strike);
            return null;
          }
          if (!strike.call || !strike.put) {
            console.warn('Missing call/put data:', strike);
            return null;
          }
          if (strike.price === null || strike.price === undefined) {
            console.warn('Missing strike price:', strike);
            return null;
          }
          
          const option = selectedType === 'call' ? strike.call : strike.put;
          if (!option) {
            console.warn('Missing option data for type:', selectedType, strike);
            return null;
          }
          
          const moneyness = option.moneyness || 'OTM';
          const styling = getMoneynessStyling(moneyness);
          const isSelected = selectedStrike?.price === strike.price;
          
          // CRITICAL: Log premium data
          console.log(`Strike ${strike.price}: ${selectedType} premium = ${option.premium}`);
          
          return (
            <div
              key={`strike-${index}-${strike.price}`}
              onClick={() => {
                try {
                  console.log('🎯 Strike selected:', strike.price, option);
                  onStrikeSelect && onStrikeSelect(strike);
                } catch (error) {
                  console.warn('Strike selection error:', error);
                }
              }}
              className={`
                ${styling.bg}
                ${styling.border}
                ${isSelected ? 'ring-2 ring-yellow-400' : ''}
                border-2 rounded-lg p-4 cursor-pointer 
                hover:scale-105 transition-transform duration-200
              `}
            >
              <div className="text-left">
                <div className="mb-3">
                  <span className={`
                    ${styling.badge} 
                    px-2 py-1 rounded text-xs font-bold
                    ${moneyness === 'ATM' ? 'animate-pulse' : ''}
                  `}>
                    {moneyness}
                  </span>
                </div>

                <div className="mb-2">
                  <div className="text-gray-400 text-xs">Strike</div>
                  <div className={`text-lg font-bold ${styling.text}`}>
                    {formatCurrency(strike.price)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">Premium ({selectedType})</div>
                  <div className={`text-sm font-medium ${styling.text}`}>
                    {formatCurrency(option.premium)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
